import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import UnitServices, { UnitProps } from 'services/hooks/unit-service';
import { Table } from '../Common/Table';
import Delete from './UnitForms/delete';
import Save from './UnitForms/save';

export default function UnitSaveMode() {
  const [getUnitsResult, setGetUnitsResult] = useState<UnitProps[] | null>([]);
  const {
    isLoading: isLoadingUnits,
    refetch,
    data: unitsData
  } = useQuery<UnitProps[], Error>('units', UnitServices.findAllUnits, {
    onError: (err: any) => {
      console.log(err.response?.data || err);
    }
  });

  useEffect(() => {
    if (unitsData && Array.isArray(unitsData)) {
      refetch();
      setGetUnitsResult(unitsData);
    }
  }, [refetch, unitsData]);

  /* The `columns` constant is an array of objects that define the columns for the table in the `UnitSaveMode` component. Each object 
  represents a column and contains properties such as `id`, `Header`, `accessor`, `Cell`, and `disableFilters`. */
  const columns = useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        disableFilters: true
      },
      {
        id: 'description',
        Header: 'Description',
        accessor: 'description',
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: UnitProps) => data,
        Cell: (row: {
          value: UnitProps;
          setEditUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setUpdateShowModal: React.Dispatch<React.SetStateAction<boolean>>;
          setDeleteUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setDeleteShowModal: React.Dispatch<React.SetStateAction<boolean>>;
        }) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    []
  );

  /* If the data is still loading or has not been fetched yet, the component returns a loading spinner component to indicate that the data is 
  being fetched. */
  if (isLoadingUnits || !getUnitsResult) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  /* The code is conditionally rendering either the `Table` component or a `LoadingSpinner` component
  based on the values of `isLoadingUnits` and `getUnitsResult`. */
  return !isLoadingUnits && getUnitsResult ? (
    <div className="unit">
      <Table
        name="Units"
        refetch={refetch}
        columns={columns}
        data={getUnitsResult}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={UnitServices.createUnit}
        updateQuery={UnitServices.updateUnit}
        deleteQuery={UnitServices.deleteUnitById}
        editQuery={UnitServices.findUnitById}
        queryName="units"
        buttonName="Unit"
      />
    </div>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
